import { useState, useEffect, useRef } from 'react';
import { Text, Button } from '@qwickapps/react-framework';
import { useAgentsList } from './useSSE';
import { config } from '../../config/runtime';

type TaskStatus = 'open' | 'in_progress' | 'blocked' | 'done';

interface Task {
  id: string;
  title: string;
  status: TaskStatus;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  /** Maps to the `owner` field in the backend items schema */
  owner?: string;
  goal_id?: string;
  blocked_reason?: string;
}

interface Goal {
  id: string;
  title: string;
}

interface KanbanBoardProps {
  tasks?: Task[];
  /** Pre-select a specific goal when the board mounts or when this value changes */
  initialGoalId?: string;
}

const columns: { id: TaskStatus; label: string; color: string }[] = [
  { id: 'open', label: 'Open', color: '#9e9e9e' },
  { id: 'in_progress', label: 'In Progress', color: '#2196f3' },
  { id: 'blocked', label: 'Blocked', color: '#f44336' },
  { id: 'done', label: 'Done', color: '#4caf50' },
];

const priorityColors = {
  low: '#9e9e9e',
  medium: '#ff9800',
  high: '#f44336',
  critical: '#9c27b0',
};

function mapMcpStatus(mcpStatus: string): TaskStatus {
  switch (mcpStatus) {
    case 'in_progress': return 'in_progress';
    case 'blocked': return 'blocked';
    case 'done': return 'done';
    default: return 'open';
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function KanbanBoard({ tasks: propTasks, initialGoalId }: KanbanBoardProps) {
  const isSelfManaged = propTasks === undefined;

  // Phase 4: goals state for per-goal task loading
  const [goals, setGoals] = useState<Goal[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(initialGoalId ?? null);
  const hasFetchedGoals = useRef(false);

  // Phase 5: interaction states
  const [internalTasks, setInternalTasks] = useState<Task[]>([]);
  const [createColumn, setCreateColumn] = useState<TaskStatus | null>(null);
  const [createTitle, setCreateTitle] = useState('');
  const [delegateTarget, setDelegateTarget] = useState<Task | null>(null);
  const [blockTarget, setBlockTarget] = useState<Task | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const { agents } = useAgentsList();

  // Phase 4: load goals via REST proxy
  useEffect(() => {
    if (!isSelfManaged || hasFetchedGoals.current) return;
    hasFetchedGoals.current = true;

    fetch(`${config.apiUrl}/api/projects/goals?status=active`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then((data: unknown) => {
        const result = data as { goals?: Array<{ id: string; title: string }> } | null;
        const goalList = Array.isArray(data) ? (data as Array<{ id: string; title: string }>) : (result?.goals ?? []);
        const mapped: Goal[] = goalList.map((g) => ({ id: g.id, title: g.title }));
        setGoals(mapped);
        // Only auto-select the first goal if no goal was pre-selected via initialGoalId
        if (mapped.length > 0 && !initialGoalId) {
          setSelectedGoalId(mapped[0].id);
        }
      })
      .catch(() => {/* stay empty */});
  }, [isSelfManaged, initialGoalId]);

  // Sync selectedGoalId whenever the caller changes initialGoalId
  useEffect(() => {
    if (initialGoalId) {
      setSelectedGoalId(initialGoalId);
    }
  }, [initialGoalId]);

  // Phase 4: load tasks per selected goal via REST proxy
  useEffect(() => {
    if (!isSelfManaged || !selectedGoalId) return;

    fetch(`${config.apiUrl}/api/projects/items?goal_id=${selectedGoalId}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then((data: unknown) => {
        const result = data as { items?: Array<{ id: string; title: string; status: string; priority?: string; owner?: string }> } | null;
        const itemList = Array.isArray(data) ? (data as Array<{ id: string; title: string; status: string; priority?: string; owner?: string }>) : (result?.items ?? []);
        const mapped: Task[] = itemList.map((item) => ({
          id: item.id,
          title: item.title,
          status: mapMcpStatus(item.status),
          priority: item.priority as Task['priority'],
          owner: item.owner,
          goal_id: selectedGoalId,
        }));
        setInternalTasks(mapped);
      })
      .catch(() => {/* stay empty */});
  }, [isSelfManaged, selectedGoalId]);

  const tasks = propTasks !== undefined ? propTasks : internalTasks;

  const getTasksByStatus = (status: TaskStatus) =>
    tasks.filter(task => task.status === status);

  // ── Create task (Phase 5: REST POST) ─────────────────────────────────────

  const handleCreateSubmit = (status: TaskStatus) => {
    const title = createTitle.trim();
    if (!title) return;
    fetch(`${config.apiUrl}/api/projects/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, status }),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then((item: Task) => {
        setInternalTasks(prev => [...prev, item]);
      })
      .catch(() => {/* silent */})
      .finally(() => {
        setCreateColumn(null);
        setCreateTitle('');
      });
  };

  // ── Patch helper (Phase 5: REST PATCH) ────────────────────────────────────

  const patchTask = (id: string, updates: Partial<Task>) => {
    fetch(`${config.apiUrl}/api/projects/items/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then((updated: Task) => {
        setInternalTasks(prev =>
          prev.map(t => (t.id === id ? { ...t, ...updated } : t)),
        );
      })
      .catch(() => {/* silent */});
  };

  // ── Delegate (Phase 5) ────────────────────────────────────────────────────

  const handleDelegate = (task: Task, agentId: string) => {
    patchTask(task.id, { owner: agentId });
    setDelegateTarget(null);
  };

  // ── Block (Phase 5) ───────────────────────────────────────────────────────

  const handleBlock = (task: Task) => {
    const reason = blockReason.trim();
    patchTask(task.id, { status: 'blocked', blocked_reason: reason || undefined });
    setBlockTarget(null);
    setBlockReason('');
  };

  // ── Approve / Reject (Phase 5) ────────────────────────────────────────────

  const handleApprove = (task: Task) => {
    patchTask(task.id, { status: 'done' });
  };

  const handleReject = (task: Task) => {
    patchTask(task.id, { status: 'open' });
  };

  return (
    <>
      {/* Phase 4: goals selector */}
      {isSelfManaged && goals.length > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
          <Text variant="body2" content="Goal:" customColor="var(--theme-text-secondary)" />
          <select
            value={selectedGoalId ?? ''}
            onChange={e => setSelectedGoalId(e.target.value || null)}
            style={{
              padding: '4px 8px',
              borderRadius: 4,
              border: '1px solid var(--theme-border-color)',
              background: 'var(--theme-bg-surface)',
              color: 'var(--theme-text-primary)',
              fontSize: 13,
            }}
          >
            {goals.map(g => (
              <option key={g.id} value={g.id}>{g.title}</option>
            ))}
          </select>
        </div>
      )}

      {/* Phase 5: 4-column kanban */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          minHeight: 400,
        }}
      >
        {columns.map(column => (
          <div
            key={column.id}
            style={{
              padding: 16,
              borderRadius: 8,
              background: 'var(--theme-bg-surface)',
              border: '1px solid var(--theme-border-color)',
            }}
          >
            {/* Column header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: column.color,
                  flexShrink: 0,
                }}
              />
              <Text variant="subtitle2" content={column.label} />
              <Text
                variant="caption"
                content={`(${getTasksByStatus(column.id).length})`}
                customColor="var(--theme-text-secondary)"
              />
              {/* Add task button */}
              <div
                onClick={() => {
                  setCreateColumn(column.id);
                  setCreateTitle('');
                }}
                data-testid={`add-task-${column.id}`}
                style={{
                  marginLeft: 'auto',
                  cursor: 'pointer',
                  color: 'var(--theme-text-secondary)',
                  fontWeight: 'bold',
                  fontSize: 18,
                  lineHeight: 1,
                  padding: '0 4px',
                }}
              >
                +
              </div>
            </div>

            {/* Phase 5: inline create form */}
            {createColumn === column.id && (
              <div
                style={{
                  marginBottom: 8,
                  padding: 8,
                  borderRadius: 4,
                  border: '1px solid var(--theme-color-primary)',
                  background: 'var(--theme-bg-secondary)',
                }}
              >
                <input
                  autoFocus
                  data-testid="create-task-input"
                  value={createTitle}
                  onChange={e => setCreateTitle(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleCreateSubmit(column.id);
                    if (e.key === 'Escape') setCreateColumn(null);
                  }}
                  placeholder="Task title…"
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'var(--theme-text-primary)',
                    fontSize: 13,
                    marginBottom: 8,
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="small" variant="primary" onClick={() => handleCreateSubmit(column.id)}>
                    Add
                  </Button>
                  <Button size="small" onClick={() => setCreateColumn(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Task cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {getTasksByStatus(column.id).map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  agents={agents}
                  delegateTarget={delegateTarget}
                  blockTarget={blockTarget}
                  blockReason={blockReason}
                  onDelegateOpen={() => setDelegateTarget(task)}
                  onDelegateSelect={agentId => handleDelegate(task, agentId)}
                  onDelegateClose={() => setDelegateTarget(null)}
                  onBlockOpen={() => {
                    setBlockTarget(task);
                    setBlockReason('');
                  }}
                  onBlockReasonChange={setBlockReason}
                  onBlockSubmit={() => handleBlock(task)}
                  onBlockClose={() => setBlockTarget(null)}
                  onApprove={() => handleApprove(task)}
                  onReject={() => handleReject(task)}
                />
              ))}
              {getTasksByStatus(column.id).length === 0 && (
                <Text
                  variant="caption"
                  content="No tasks"
                  customColor="var(--theme-text-disabled)"
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Phase 5: delegate overlay backdrop */}
      {delegateTarget && (
        <div
          onClick={() => setDelegateTarget(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
          }}
        />
      )}
    </>
  );
}

// ── TaskCard ──────────────────────────────────────────────────────────────────

interface TaskCardProps {
  task: Task;
  agents: { id: string; name: string }[];
  delegateTarget: Task | null;
  blockTarget: Task | null;
  blockReason: string;
  onDelegateOpen: () => void;
  onDelegateSelect: (agentId: string) => void;
  onDelegateClose: () => void;
  onBlockOpen: () => void;
  onBlockReasonChange: (r: string) => void;
  onBlockSubmit: () => void;
  onBlockClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

function TaskCard({
  task,
  agents,
  delegateTarget,
  blockTarget,
  blockReason,
  onDelegateOpen,
  onDelegateSelect,
  onDelegateClose,
  onBlockOpen,
  onBlockReasonChange,
  onBlockSubmit,
  onBlockClose,
  onApprove,
  onReject,
}: TaskCardProps) {
  const isDelegating = delegateTarget?.id === task.id;
  const isBlocking = blockTarget?.id === task.id;

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 4,
        background: 'var(--theme-bg-secondary)',
        border: '1px solid var(--theme-border-color)',
        position: 'relative',
      }}
    >
      {task.priority && (
        <div
          style={{
            width: 4,
            height: '100%',
            position: 'absolute',
            left: 0,
            top: 0,
            borderRadius: '4px 0 0 4px',
            background: priorityColors[task.priority],
          }}
        />
      )}
      <Text variant="body2" content={task.title} />
      {task.owner && (
        <Text
          variant="caption"
          content={task.owner}
          customColor="var(--theme-text-secondary)"
        />
      )}

      {/* Action row */}
      <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
        <Button size="small" onClick={onDelegateOpen} data-testid="delegate-btn">
          Delegate
        </Button>
        {task.status !== 'blocked' && task.status !== 'done' && (
          <Button size="small" onClick={onBlockOpen} data-testid="block-btn">
            Block
          </Button>
        )}
        {(task.status === 'blocked' || task.status === 'in_progress') && (
          <>
            <Button
              size="small"
              variant="primary"
              onClick={onApprove}
              data-testid="approve-btn"
            >
              Approve
            </Button>
            <Button size="small" onClick={onReject} data-testid="reject-btn">
              Reject
            </Button>
          </>
        )}
      </div>

      {/* Delegate dropdown */}
      {isDelegating && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 200,
            marginTop: 4,
            borderRadius: 4,
            background: 'var(--theme-bg-surface)',
            border: '1px solid var(--theme-border-color)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          }}
        >
          {agents.length === 0 ? (
            <div style={{ padding: 8 }}>
              <Text variant="caption" content="No agents available" customColor="var(--theme-text-disabled)" />
            </div>
          ) : (
            agents.map(agent => (
              <div
                key={agent.id}
                onClick={() => onDelegateSelect(agent.id)}
                data-testid={`delegate-agent-${agent.id}`}
                style={{
                  padding: 8,
                  cursor: 'pointer',
                }}
              >
                <Text variant="body2" content={agent.name} />
              </div>
            ))
          )}
          <div
            onClick={onDelegateClose}
            style={{
              padding: 8,
              cursor: 'pointer',
              borderTop: '1px solid var(--theme-border-color)',
            }}
          >
            <Text variant="caption" content="Cancel" customColor="var(--theme-text-secondary)" />
          </div>
        </div>
      )}

      {/* Block reason form */}
      {isBlocking && (
        <div style={{ marginTop: 8 }}>
          <input
            autoFocus
            data-testid="block-reason-input"
            value={blockReason}
            onChange={e => onBlockReasonChange(e.target.value)}
            placeholder="Reason for blocking…"
            style={{
              width: '100%',
              background: 'var(--theme-bg-surface)',
              border: '1px solid var(--theme-border-color)',
              borderRadius: 4,
              outline: 'none',
              color: 'var(--theme-text-primary)',
              fontSize: 12,
              padding: '4px 8px',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            <Button size="small" variant="primary" onClick={onBlockSubmit}>
              Confirm Block
            </Button>
            <Button size="small" onClick={onBlockClose}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
